interface UtmHash {
  timestamp: number,
  [key: string]: string | number,
}

class UtmReader {
  
  static TARGET_METRICS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
  ];
  
  static LS_KEY = 'utm_hash';
  
  /**
   * Retrives metrix previously stored in LS
   * @return returns json-string of utm data or null if not exists
   */
  public static getStoredMetrix(): UtmHash | null {
    const stored = localStorage.getItem(this.LS_KEY);
    return stored ? JSON.parse(stored) : null;
  }
  
  /**
   * modernizr's approach to test localStorage availability
   * @return flag that shows localstorage availability
   */
  static isLocalStorage(): boolean {
    try {
      const testString = 'test';
      localStorage.setItem(testString, testString);
      localStorage.removeItem(testString);
      return true;
    } catch (error) {
      Sentry.captureException(error);
      return false;
    }
  }
  
  
  /**
   * Gets `utm_*` metrics and stores in a LocalStorage,
   * @param  query - query string like `?utm_source=twitter&utm_medium=email&utm_campaign=promo`
   */
  static collectMetrics(query: string): void {
    if (!this.isLocalStorage()) return;
    if (!query && !/utm_/.test(query)) return;
    
    const previous: UtmHash | null = this.getStoredMetrix();
    const currentTs = Date.now();
    let updateMark = false;
        
    if (previous) {
      // NOTE: according to task UTM-data should be updated in LS is
      // update metrics once a month https://uchiru2.atlassian.net/browse/FEC-144
      if (this.isMonthPassed(previous.timestamp, currentTs)) updateMark = true;
    }
    
    if (previous === null || updateMark) {
      const utmHash: UtmHash = this.getMetricsFromQuery(query, currentTs);
      localStorage.setItem(this.LS_KEY, JSON.stringify(utmHash));
    }
  }
  
  /**
   * Splits query parameters and filters them to get only `utm_*` parameters,
   * defined in `TARGET_METRICS`
   * @param  query - query string like `?utm_source=twitter&utm_medium=email&utm_campaign=promo`
   * @return     `UtmHash` object
   */
  static getMetricsFromQuery(query: string, timestamp?: number): UtmHash {
    const urlParams = new URLSearchParams(query);
    const result: UtmHash = {
      timestamp: timestamp ? timestamp : Date.now()
    };
    
    urlParams.forEach((value, key) => {
      if (this.TARGET_METRICS.includes(key)) {
        result[key] = value;
      }
    });
    return result;
  }

  /**
   * Returns boolean flag, that shows if month was changed
   * @param  t1 - unix timestamp to compare
   * @param  t2 - unix timestamp to compare
   */
  static isDifferentMonth(t1: number, t2: number): boolean {
    const monthGetter = (ts: number) => {
      const date = new Date(ts);
      return date.getMonth();
    }
    return monthGetter(t1) !== monthGetter(t2);
  }

  /**
   * Returns boolean flag, that shows if 30 days had passed from `t1` to `t2`
   * @param  t1 - unix timestamp to compare, should be less than t2
   * @param  t2 - unix timestamp to compare
   */
  static isMonthPassed(t1: number, t2: number): boolean {
    const msecPerDay = 1000 * 3600 * 24;
    return Math.floor((t2 - t1) / msecPerDay) > 30;
  }
}


export default UtmReader;
